import type { PropsWithChildren } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { type CalendarDateTime, Time } from '@internationalized/date';
import type { DateValue } from 'react-aria-components/Calendar';
import { Dialog, DialogTrigger } from 'react-aria-components/Dialog';

import {
  applyTimeToDatetime,
  createTimeFromInputs,
  DEFAULT_TIME,
  ensureDateTime,
  formatHourForDisplay,
  getTimeInputsFromDatetime,
  LEGACY_TIME_FORMAT_STORAGE_KEY,
  PLACEHOLDER_DATETIME,
  resolveHoursInput,
  resolveOpenTimeFormat,
  TIME_FORMAT_STORAGE_KEY,
  to24HourFromDisplay,
} from '../../utils/datetime_draft';
import {
  resolveInitialTimeFormat,
  setPersistedTimeFormat,
} from '../../utils/persisted_time_format';
import { Button } from '../button/button';
import { Calendar } from '../calendars/calendar';
import { Popover, type PopoverProps } from '../popover/popover';
import { Separator } from '../separator/separator';
import { Stack } from '../stack/stack';
import { type TimeFormatType, TimePicker } from '../time_picker/time_picker';

import styles from './datetime_calendar_popover.module.css';

export type DatetimeCalendarPopoverProps = PropsWithChildren<{
  value?: CalendarDateTime | null;
  defaultValue?: CalendarDateTime | null;
  onChange?: (value: CalendarDateTime) => void;
  onOpenChange?: (isOpen: boolean) => void;
  minValue?: DateValue;
  maxValue?: DateValue;
  applyLabel?: string;
  showSeconds?: boolean;
  placement?: PopoverProps['placement'];
  offset?: number;
  /** Seeds the initial 12h/24h choice; falls back to the persisted preference. */
  hourCycle?: 12 | 24;
}>;

/**
 * A date and time picker rendered in a popover, without an accompanying text
 * input. `children` is the trigger the popover is anchored to.
 *
 * Use `DatetimeField` instead when the selected value should also be editable
 * as text.
 *
 * ```tsx
 * <DatetimeCalendarPopover applyLabel="Schedule" onChange={handleChange}>
 *   <Button>Schedule</Button>
 * </DatetimeCalendarPopover>
 * ```
 */
export function DatetimeCalendarPopover({
  children,
  value,
  defaultValue,
  onChange,
  onOpenChange,
  minValue,
  maxValue,
  applyLabel = 'Apply',
  showSeconds = true,
  placement = 'top',
  offset = 8,
  hourCycle,
}: DatetimeCalendarPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [uncontrolledValue, setUncontrolledValue] =
    useState<CalendarDateTime | null>(defaultValue ?? null);

  const committedValue =
    value !== undefined ? (value ?? null) : uncontrolledValue;

  const [draftValue, setDraftValue] = useState<CalendarDateTime | null>(
    committedValue,
  );

  const [appliedFormat, setAppliedFormat] = useState<TimeFormatType>(() =>
    resolveInitialTimeFormat({
      hourCycle,
      storageKey: TIME_FORMAT_STORAGE_KEY,
      legacyStorageKey: LEGACY_TIME_FORMAT_STORAGE_KEY,
    }),
  );
  const [draftFormat, setDraftFormat] = useState<TimeFormatType>(appliedFormat);

  const [hours, setHours] = useState('12');
  const [minutes, setMinutes] = useState('00');
  const [seconds, setSeconds] = useState('00');

  const normalizedMinValue = useMemo(
    () => ensureDateTime(minValue, new Time(0, 0, 0)) ?? undefined,
    [minValue],
  );
  const normalizedMaxValue = useMemo(
    () => ensureDateTime(maxValue, new Time(23, 59, 59)) ?? undefined,
    [maxValue],
  );

  const handleFormatChange = useCallback(
    (nextFormat: TimeFormatType) => {
      const currentHourIn24Format = to24HourFromDisplay(hours, draftFormat);

      setDraftFormat(nextFormat);
      setHours(formatHourForDisplay(currentHourIn24Format, nextFormat));
    },
    [draftFormat, hours],
  );

  const createTimeFromDraftInputs = useCallback(
    () =>
      createTimeFromInputs(
        { hours, minutes, seconds: showSeconds ? seconds : '00' },
        draftFormat,
      ),
    [draftFormat, hours, minutes, seconds, showSeconds],
  );

  const syncTimeInputsFromDraft = useCallback(
    (draft: CalendarDateTime) => {
      const timeInputs = getTimeInputsFromDatetime(draft, draftFormat);

      setHours(timeInputs.hours);
      setMinutes(timeInputs.minutes);
      setSeconds(timeInputs.seconds);
    },
    [draftFormat],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        const base = committedValue ?? PLACEHOLDER_DATETIME;
        const openFormat = resolveOpenTimeFormat(appliedFormat, base);
        const timeInputs = getTimeInputsFromDatetime(base, openFormat);

        setDraftValue(committedValue);
        setDraftFormat(openFormat);
        setHours(timeInputs.hours);
        setMinutes(timeInputs.minutes);
        setSeconds(timeInputs.seconds);
      }

      setIsOpen(open);
      onOpenChange?.(open);
    },
    [appliedFormat, committedValue, onOpenChange],
  );

  const handleApply = useCallback(() => {
    if (!draftValue) {
      return;
    }

    const next = applyTimeToDatetime(draftValue, createTimeFromDraftInputs());

    if (value === undefined) {
      setUncontrolledValue(next);
    }
    onChange?.(next);

    setAppliedFormat(draftFormat);
    setPersistedTimeFormat(TIME_FORMAT_STORAGE_KEY, draftFormat);
    setIsOpen(false);
    onOpenChange?.(false);
  }, [
    createTimeFromDraftInputs,
    draftFormat,
    draftValue,
    onChange,
    onOpenChange,
    value,
  ]);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      {children}
      <Popover className={styles.popover} placement={placement} offset={offset}>
        <Dialog className={styles.dialog}>
          <Stack gap="2xl">
            <Calendar<CalendarDateTime>
              value={draftValue}
              minValue={normalizedMinValue}
              maxValue={normalizedMaxValue}
              onChange={(nextValue) => {
                const next = ensureDateTime(nextValue, DEFAULT_TIME);
                if (!next) {
                  return;
                }

                const preserved = applyTimeToDatetime(
                  next,
                  createTimeFromDraftInputs(),
                );

                setDraftValue(preserved);
                syncTimeInputsFromDraft(preserved);
              }}
            />

            <Separator variant="sub" />

            <TimePicker
              format={draftFormat}
              onFormatChange={handleFormatChange}
              hours={hours}
              minutes={minutes}
              onHoursChange={(key) => {
                setHours(resolveHoursInput(key, draftFormat, hours));
              }}
              onMinutesChange={(key) => {
                setMinutes(String(key ?? '00'));
              }}
              showSeconds={showSeconds}
              seconds={seconds}
              onSecondsChange={(key) => {
                setSeconds(String(key ?? '00'));
              }}
            />

            <Separator variant="sub" />

            <div className={styles.apply_row}>
              <Button size="m" onPress={handleApply} isDisabled={!draftValue}>
                {applyLabel}
              </Button>
            </div>
          </Stack>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
